import _ from 'lodash';
import moment from 'moment';
import { DateConstants } from '@/models/Constants';
import swal from 'sweetalert2';
import { ApiResult } from '@/services/BaseApi';
import { AwaitTaskResponse } from '@/models/Interfaces';
import q from 'q';
import { useI18n } from 'vue-i18n';
export function createDebounce() {
    let timeout = 0;
    return function(fnc: any, delayMs?: number) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            fnc();
        }, delayMs || 400);
    };
}
function getChangedKeys(o1: any, o2: any) {
    const keys = _.union(_.keys(o1), _.keys(o2));
    return _.filter(keys, function(key) {
        return o1[key] !== o2[key];
    });
}
export function getFlagChanges<T extends object, E extends Record<keyof E, number>>(
    origin: T,
    updated: T,
    enumType: E,
    specialKeyMappingFn?: (key: string) => keyof E | null
): number {
    const keysChanged = getChangedKeys(origin, updated);
    const changeFlag = keysChanged.reduce((acc, key) => {
        let enumKey: keyof E | null = null;
        if (specialKeyMappingFn) {
            enumKey = specialKeyMappingFn(key);
        }

        enumKey = enumKey || ((key.charAt(0).toUpperCase() + key.slice(1)) as keyof E);
        const enumValue = enumType[enumKey];

        if (enumValue !== undefined && (acc & enumValue) === 0) {
            return acc | enumValue;
        }
        return acc;
    }, 0);

    return changeFlag;
}
export function getBirthdateString(date: Date | null | string) {
    if ((date instanceof Date && !isNaN(date.getTime())) == false) {
        return null;
    }
    return moment(date).format(DateConstants.SERVER_NODATIME_LOCAL_DATE_FORMAT);
}

export async function awaitTask(
    action: () => Promise<ApiResult<AwaitTaskResponse>>,
    onSuccess: (url: string) => void,
    processingText: string,
    waitTime = 3000
) {
    swal.fire(processingText);
    swal.showLoading();
    await q.delay(waitTime);

    const resp = await action();
    if (resp.errorMessage) {
        await swal.fire({
            icon: 'error',
            text: resp.errorMessage
        });
        return;
    }
    if (resp.data?.url) {
        onSuccess(resp.data.url);
        return;
    }

    if (waitTime > 0) {
        awaitTask(action, onSuccess, processingText, waitTime);
    }
}

export function getTimezone() {
    return -new Date().getTimezoneOffset();
}


// Input can be "HH:mm", "HH:mm:ss", null, or empty → always returns "HH:mm:ss".
export function toHHMMSS(input?: string | null): string {
    const DEFAULT = '00:00:00';

    if (!input) {
        return DEFAULT;
    }

    const trimmed = input.trim();
    if (trimmed.length === 0) {
        return DEFAULT;
    }

    const parts = trimmed.split(':');

    // Helper: ensure two-digit padding (e.g. "5" -> "05")
    const pad2 = (v: string) => v.trim().padStart(2, '0');

    // 4) Handle "HH:mm" → add ":00" for seconds
    if (parts.length === 2) {
        const [h, m] = parts;
        if (!h || !m) {
            return DEFAULT;
        }
        return `${pad2(h)}:${pad2(m)}:00`;
    }

    // 5) Handle "HH:mm:ss" → normalize padding
    if (parts.length === 3) {
        const [h, m, s] = parts;
        if (!h || !m || !s) {
            return DEFAULT;
        }
        return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
    }

    return DEFAULT;
}
